import React, { useState, useEffect } from 'react'
import axios from 'axios'
import V2Layout from '../components/V2Layout'
import SEO from '../components/SEO'

const API = ''

function formatDate(d) {
    if (!d) return ''
    return new Date(d + 'T00:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}

const CertificationsScreen = () => {
    const [certs, setCerts] = useState([])
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        axios.get(`${API}/api/certifications`)
            .then(r => {
                // Filter certifications to only show those active on Certifications page
                const filtered = r.data.filter(c => c.show_on_certifications === 1)
                setCerts(filtered)
                setLoading(false)
            })
            .catch(err => {
                console.error('Error loading certifications:', err)
                setLoading(false)
            })
    }, [])

    return (
        <V2Layout pageClass="pfc-certifications">
            <SEO
                title="Quality & Compliance Standards"
                description="Precision Fluid Controls operates under a rigorous quality control framework. Our facility, systems, and procedures are certified by leading standard bodies."
            />
            <section className="pfc-page-hero">
                <div className="pfc-page-hero-inner">
                    <p className="v2-section-label">Quality</p>
                    <h1 className="pfc-page-hero-title">Quality &amp; Compliance Standards</h1>
                    <p className="pfc-page-hero-sub">
                        Precision Fluid Controls operates under a rigorous quality control framework. 
                        Our facility, systems, and procedures are certified by leading standard bodies 
                        to ensure compliance for demanding aerospace, military, and cryogenic applications.
                    </p>
                </div>
            </section>

            {/* Commented out in case we decide to roll back:
            <section className="v2-content">
                <div className="v2-content-inner">
                    <div className="v2-content-col">
                        <p className="v2-section-label">Overview</p>
                        <h2 className="v2-content-title">Aerospace-Grade Standards</h2>
                        <p className="v2-content-body">
                            Our quality management systems are designed around strict aerospace and defense standards,
                            ensuring full product traceability, rigorous testing, and continuous improvement. 
                            We undergo regular audits to maintain certifications across our manufacturing processes.
                        </p>
                        <p className="v2-content-body">
                            Every component shipped from our facility is accompanied by extensive compliance documentation,
                            material test reports (MTR), and inspection logs as required.
                        </p>
                        <div className="v2-cert-badges">
                            {loading ? (
                                <span className="v2-cert-badge">Loading...</span>
                            ) : certs.length > 0 ? (
                                certs.map(c => (
                                    <span className="v2-cert-badge" key={c.id}>{c.code}</span>
                                ))
                            ) : (
                                <span className="v2-cert-badge">No active standards</span>
                            )}
                        </div>
                    </div>
                    <div className="v2-content-col">
                        <p className="v2-section-label">Quality Assurance</p>
                        <h2 className="v2-content-title">Audit &amp; Testing Capabilities</h2>
                        <ul className="v2-spec-list">
                            <li>100% inspection on critical component dimensions</li>
                            <li>Full raw material traceability back to original melt</li>
                            <li>ISO 7 (Class 10,000) hard-wall cleanroom assembly &amp; packaging (NASA-approved)</li>
                            <li>Helium leak testing and high-pressure pneumatic proof testing</li>
                            <li>Oxygen cleanliness and passivation capabilities</li>
                        </ul>
                    </div>
                </div>
            </section>
            */}

            <section className="v2-approach" style={{ paddingBottom: '5rem' }}>
                <div className="v2-approach-inner" style={{ maxWidth: '800px', margin: '0 auto' }}>
                    {loading ? (
                        <div className="text-center py-5" style={{ color: 'var(--v2-primary)', fontFamily: 'var(--pfc-mono, monospace)' }}>
                            Loading quality standards...
                        </div>
                    ) : certs.length === 0 ? (
                        <div className="text-center py-5 text-muted" style={{ fontStyle: 'italic', color: 'var(--v2-text2)' }}>
                            No active quality standards listed at this time.
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '3rem', marginTop: '2rem' }}>
                            {certs.map((c, index) => (
                                <div key={c.id} style={{ borderBottom: '1px solid var(--v2-border)', paddingBottom: '2.5rem' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.75rem' }}>
                                        <h3 style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--v2-primary)', margin: 0 }}>
                                            {c.code} <span style={{ fontWeight: 400, color: 'var(--v2-text2)', fontSize: '1.2rem' }}>— {c.title}</span>
                                        </h3>
                                        {c.issuing_body && (
                                            <span style={{ fontSize: '11px', fontFamily: 'var(--pfc-mono, monospace)', color: 'var(--v2-text3)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                Issued by {c.issuing_body}
                                            </span>
                                        )}
                                    </div>

                                    <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--v2-text2)', marginBottom: '1rem', maxWidth: '750px' }}>
                                        {c.description}
                                    </p>

                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
                                        <div style={{
                                            fontSize: '11px',
                                            color: 'var(--v2-text3)',
                                            fontFamily: 'var(--pfc-mono, monospace)',
                                            textTransform: 'uppercase',
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            gap: '12px'
                                        }}>
                                            {c.location && <span>📍 Location: {c.location}</span>}
                                            {c.date_issued && <span>📅 Issued: {formatDate(c.date_issued)}</span>}
                                            {c.expiry_date && <span>⏳ Expires: {formatDate(c.expiry_date)}</span>}
                                        </div>

                                        {c.file_path && (
                                            <a
                                                href={`${API}/${c.file_path}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '6px',
                                                    fontSize: '12px',
                                                    fontWeight: 700,
                                                    color: 'var(--v2-primary)',
                                                    textDecoration: 'none',
                                                    borderBottom: '1px solid var(--v2-primary)',
                                                    paddingBottom: '2px',
                                                    transition: 'opacity 0.2s ease'
                                                }}
                                                onMouseEnter={e => e.currentTarget.style.opacity = 0.8}
                                                onMouseLeave={e => e.currentTarget.style.opacity = 1}
                                            >
                                                View Certificate ↗
                                            </a>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </section>
        </V2Layout>
    )
}

export default CertificationsScreen
